<template>
  <div class="fill-height">
    <v-card class="pa-5 rounded-lg fill-height">
      <v-row>
        <v-col>
          <div class="text-h6">
            {{ $t("card_title.raw_data") }}
          </div>
        </v-col>
        <v-col
          v-if="removable"
          cols="auto"
        >
          <v-btn
            icon
            style="pointer-events: auto"
            @click="$emit('remove')"
          >
            <v-icon small>
              fal fa-times
            </v-icon>
          </v-btn>
        </v-col>
        <v-col
          v-if="hasExerciseTimings"
          cols="auto"
        >
          <v-btn
            small
            :outlined="!showExercises"
            color="primary"
            class="text-none"
            @click="showPeriods = false; showExercises = !showExercises"
          >
            {{ $t("button.show_exercises") }}
          </v-btn>
        </v-col>
        <v-col
          v-if="hasPeriods"
          cols="auto"
        >
          <v-btn
            small
            :outlined="!showPeriods"
            color="primary"
            class="text-none"
            @click="showPeriods = !showPeriods; showExercises = false"
          >
            {{ $t("button.show_periods") }}
          </v-btn>
        </v-col>
      </v-row>
      <div
        v-if="rawData.loading"
        class="fill-height d-flex align-center justify-center"
      >
        <v-progress-circular
          color="grey lighten-1"
          :size="150"
          indeterminate
          :width="1"
        />
      </div>
      <div
        v-else-if="speedSeries.length === 0 && heartRateLogSeries.length === 0"
        class="text-center fill-height d-flex align-center justify-center"
      >
        {{ $t("data_empty") }}
      </div>
      <div v-else>
        <line-chart
          class="elevation-0"
          :series-override="[{ name: $t('performance.raw.variables.heart_rate'), data: heartRateLogSeries },{ name: $t('performance.raw.variables.speed'), data: speedSeries }]"
          :xaxis="{ type: 'datetime', labels: { datetimeUTC: false } }"
          :yaxis="[ { title: { text: $t('hr_variables.hr_bpm') }, max: 210, min: 0, seriesName: 'Heart Rate' }, { title: { text: $t('gps_variables.speed_unit') }, min: 0, seriesName: 'Speed', opposite: true } ]"
          :tooltip="{
            x: {
              format: getDateTimeFormatForUser('HH:mm:ss')
            }
          }"
          :animations="{enabled: false}"
          :colors="['#E91E63', '#008FFB']"
          :annotations="heartRateLogAnnotations"
          :height="350"
          :fill="{ type: 'solid', opacity: [1, 1] }"
        />
      </div>
    </v-card>
  </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import dayjs from 'dayjs'
import { EventApiService } from '@/data/services'
import { getDateTimeFormatForUser, hasSixZoneSetup } from '@/utils/helpers'

export default {
  name: 'RawDataChart',
  components: { LineChart },
  props: {
    player: {
      type: Object,
      required: true
    },
    event: {
      type: Object,
      required: true
    },
    removable: {
      type: Boolean,
      default: false
    },
    parentEventId: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    rawData: {
      data: [],
      loading: false
    },
    playerHrProfile: null,
    showPeriods: false,
    showExercises: false
  }),
  computed: {
    // Raw data should always use parent event ID when viewing a period
    eventIdForRawData() {
      return this.parentEventId || this.event.data.id
    },
    hasExerciseTimings() {
      return this.event.data.training_exercises && this.event.data.training_exercises.length > 0 &&
        this.event.data.training_exercises.some(exercise => exercise.exercise_start_at && exercise.exercise_stop_at)
    },
    hasPeriods() {
      return this.event.data.periods && this.event.data.periods.length > 0
    },
    heartRateLogAnnotations() {
      const hrProfile = this.playerHrProfile

      const method = hrProfile?.method

      let addKey = ''

      if (method === 'HRANP') addKey = 'ANPM'

      const isSixZone = hasSixZoneSetup()

      const zones = isSixZone
        ? [
          { key: 'HRREG', color: '#bfbfbf', label: this.$t('variables.hr_zone_n', { n: 1 }) },
          { key: 'HRRE', color: '#008FFB', label: this.$t('variables.hr_zone_n', { n: 2 }) },
          { key: 'HRAI', color: '#00E396', label: this.$t('variables.hr_zone_n', { n: 3 }) },
          { key: 'HRANP', color: '#FEB019', label: this.$t('variables.hr_zone_n', { n: 4 }) },
          { key: 'HRVO2MAX', color: '#FF4560', label: this.$t('variables.hr_zone_n', { n: 5 }) },
          { key: 'ZONE_6', color: '#775DD0', label: this.$t('variables.hr_zone_n', { n: 6 }), noSuffix: true }
        ]
        : [
          { key: 'HRREG', color: '#bfbfbf', label: this.$t('hr_variables.regeneration_zone') },
          { key: 'HRRE', color: '#008FFB', label: this.$t('hr_variables.extensive_aerobic_zone') },
          { key: 'HRAI', color: '#00E396', label: this.$t('hr_variables.intensive_aerobic_zone') },
          { key: 'HRANP', color: '#FEB019', label: this.$t('hr_variables.anaerobic_threshold_zone') },
          { key: 'HRVO2MAX', color: '#FF4560', label: this.$t('hr_variables.vo2_max_zone') }
        ]

      return {
        yaxis: (!hrProfile || !this.rawData.data.HEART_RATE.length) ? [] : zones.flatMap(z => {
          const zoneKey = z.noSuffix ? z.key : z.key + addKey
          const range = hrProfile[zoneKey]
          if (!range) return []
          return [{
            y: range.lower,
            y2: range.upper,
            opacity: 0.5,
            fillColor: z.color,
            borderColor: z.color,
            label: {
              text: z.label,
              offsetY: 17,
              offsetX: -5,
              style: {
                background: 'transparent'
              }
            }
          }]
        }),
        xaxis: this.xAxisAnnotations
      }
    },
    speedSeries() {
      const data = this.rawData.data.SPEED ?? []
      const timestamps = this.rawData.data.TIMESTAMP ?? []
      const series = []

      const dateStart = dayjs(this.event.data.start_at)
      const dateEnd = dayjs(this.event.data.end_at)

      if (data.length > 0) {
        series.push([dateStart.valueOf(), null])
      }

      for (const [index, d] of data.entries()) {
        const timestamp = timestamps[index]
          ? timestamps[index] * 1000
          : dateStart.add(index, 'second').valueOf()

        series.push([timestamp, d])
      }

      if (data.length > 0) {
        series.push([dateEnd.valueOf(), null])
      }

      return series
    },
    heartRateLogSeries() {
      const data = this.rawData.data.HEART_RATE ?? []
      const timestamps = this.rawData.data.TIMESTAMP ?? []
      const series = []

      const dateStart = dayjs(this.event.data.start_at)
      const dateEnd = dayjs(this.event.data.end_at)

      if (data.length > 0) {
        series.push([dateStart.valueOf(), null])
      }

      for (const [index, d] of data.entries()) {
        const timestamp = timestamps[index]
          ? timestamps[index] * 1000
          : dateStart.add(index, 'second').valueOf()

        series.push([timestamp, d])
      }

      if (data.length > 0) {
        series.push([dateEnd.valueOf(), null])
      }

      return series
    },
    xAxisAnnotations() {
      if (this.showPeriods) return this.periodAnnotations

      if (this.showExercises) return this.exerciseAnnotations

      return []
    },
    exerciseAnnotations() {
      const returnList = []
      const eventDate = dayjs(this.event.data.start_at)

      const processExercise = (exercise) => {
        if (!exercise.exercise_start_at || !exercise.exercise_stop_at) return

        const startTime = dayjs(exercise.exercise_start_at)
        const stopTime = dayjs(exercise.exercise_stop_at)

        returnList.push({
          x: eventDate.hour(startTime.hour()).minute(startTime.minute()).utc().valueOf(),
          x2: eventDate.hour(stopTime.hour()).minute(stopTime.minute()).utc().valueOf(),
          fillColor: '#e3f2fd',
          borderColor: '#BC291C',
          opacity: 0.3,
          label: {
            text: exercise.name || `${exercise.type.charAt(0).toUpperCase() + exercise.type.slice(1)}`,
            style: {
              background: '#BC291C',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 'bold'
            }
          }
        })
      }

      this.event.data.training_exercises.forEach(exercise => {
        switch (exercise.type) {
          case 'exercise':
            processExercise(exercise)
            break

          case 'group':
            if (exercise.exercises && exercise.exercises.length > 0) {
              exercise.exercises.forEach(groupExercise => processExercise(groupExercise))
            }
            break

          case 'superset':
            processExercise(exercise)
            break

          case 'pause':
            processExercise({
              ...exercise,
              name: this.$t('training_builder.pause')
            })
            break

          default:
            processExercise(exercise)
            break
        }
      })

      return returnList
    },
    periodAnnotations() {
      const returnList = []

      this.event.data.periods.forEach(period => {
        returnList.push({
          x: dayjs(period.start_at).utc().valueOf(),
          x2: dayjs(period.end_at).utc().valueOf(),
          fillColor: '#e3f2fd',
          borderColor: '#BC291C',
          opacity: 0.3,
          label: {
            text: period.title,
            style: {
              background: '#BC291C',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 'bold'
            }
          }
        })
      })

      return returnList
    }
  },
  mounted() {
    if (!this.removable) {
      this.fetchPlayerRawData()
      this.fetchPlayerHrProfile()
    } else {
      this.rawData.data = {
        HEART_RATE: Array.from({ length: 100 }, () => {
          return Math.round(60 + Math.sin(Math.random() * Math.PI) * 120)
        }),
        SPEED: Array.from({ length: 100 }, (_, i) => {
          const phase = Math.floor(i / 20)
          const baseSpeed = phase % 3 === 0 ? 5 : phase % 3 === 1 ? 25 : 15
          return Math.round(baseSpeed + (Math.random() - 0.5) * 10)
        }),
        TIMESTAMP: Array.from({ length: 100 }, (_, i) => this.$dayjs() - (99 - i) * 1000)
      }
    }
  },
  methods: {
    getDateTimeFormatForUser,
    async fetchPlayerRawData() {
      this.rawData.loading = true

      try {
        const params = {
          player_id: this.player.id,
          data_type: 'raw'
        }

        const data = await EventApiService.getPlayerPerformanceData(this.eventIdForRawData, params)

        this.rawData.data = { ...this.rawData.data, ...data }
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: 'There were some errors loading player stats',
          color: 'error'
        })

        throw e
      } finally {
        this.rawData.loading = false
      }
    },
    async fetchPlayerHrProfile() {
      try {
        const stats = await EventApiService.getPlayerStats(this.eventIdForRawData, {
          player_ids: [this.player.id],
          stats_types_variables: {
            hr: ['player_hr_profile']
          }
        })

        if (stats && stats.length > 0) {
          this.playerHrProfile = stats[0].stats?.player_hr_profile
        }
      } catch (e) {
        // Silently fail - HR profile annotations are optional
      }
    }
  }
}
</script>
